import { discountLabel, eventTotals } from './totals';
import { resolveDepositEuro } from './deposit';
import { clientNotes, hasPriceDisclaimer } from './quote';
import { hasVariants, variantEvent, variantList } from './quote-variants';
import translationsIT from '../../../public/i18n/it.json';
import translationsEN from '../../../public/i18n/en.json';
import translationsES from '../../../public/i18n/es.json';

export type MailLang = 'it' | 'en' | 'es';

const DICTS: Record<MailLang, any> = { it: translationsIT, en: translationsEN, es: translationsES };
export const MAIL_LOCALES: Record<MailLang, string> = { it: 'it-IT', en: 'en-GB', es: 'es-ES' };

// Traduttore indipendente dalla lingua attiva dell'app: serve a scrivere la mail in un'altra lingua
export function translatorFor(lang: MailLang): (key: string, params?: any) => string {
  return (key, params) => {
    const found = key.split('.').reduce((o: any, k) => o?.[k], DICTS[lang]);
    const text = typeof found === 'string' ? found : key;
    return text.replace(/{{\s*(\w+)\s*}}/g, (_m, p) => String(params?.[p] ?? ''));
  };
}

export interface QuoteEmailInput {
  event: any;
  t: (key: string, params?: any) => string;
  locale: string;
  dateDisplay: string;
  isBeauty: boolean;
  link: string | null;
  linkExpiry: string;
  signature: { name: string; phone: string; email: string };
  logo?: string | null;
}

interface Row {
  label: string;
  desc: string;
  amount: number;
  full: number | null;
}

const esc = (s: string) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function rowsOf(event: any, t: QuoteEmailInput['t'], isBeauty: boolean): Row[] {
  const totals = eventTotals(event);
  const rows: Row[] = (event?.event?.services ?? [])
    .filter((s: any) => s.selected)
    .map((s: any) => {
      const qty = +s.quantity || 1;
      const full = +s.full_price > +s.price ? +s.full_price * qty : null;
      const desc = [s.description, full ? s.discount_name : ''].filter(Boolean).join(' · ');
      return {
        label: `${s.name}${qty > 1 ? ` ×${qty}` : ''}`,
        desc,
        amount: (+s.price || 0) * qty,
        full,
      };
    });
  if (totals.test > 0) {
    rows.push({ label: t(isBeauty ? 'quote.page.trial' : 'quote.page.inspection'), desc: '', amount: totals.test, full: null });
  }
  if (totals.travel > 0) {
    rows.push({ label: t('quote.page.travel'), desc: '', amount: totals.travel, full: null });
  }
  return rows;
}

/** Righe, totali e acconto di un evento (o di una proposta), in HTML e in testo. */
function pricesOf(event: any, i: QuoteEmailInput, money: (n: number) => string): { html: string; text: string[] } {
  const { t } = i;
  const totals = eventTotals(event);
  const rows = rowsOf(event, t, i.isBeauty);
  const deposit = resolveDepositEuro(event?.financial?.deposit, totals.total);
  const hasDiscount = totals.discount > 0;
  const discLabel = discountLabel(event, t('quote.page.discount'));

  const cell = 'padding:8px 0;border-bottom:1px solid #e8e2d9;font-size:14px;color:#2b2b2b;';
  const rowsHtml = rows
    .map(
      (r) => `<tr>
<td style="${cell}">${esc(r.label)}${r.desc ? `<br><span style="font-size:12px;color:#7a7266;">${esc(r.desc)}</span>` : ''}</td>
<td style="${cell}text-align:right;white-space:nowrap;">${r.full ? `<span style="color:#9a9285;text-decoration:line-through;margin-right:8px;">${money(r.full)}</span>` : ''}${money(r.amount)}</td>
</tr>`,
    )
    .join('');

  const sumRow = (label: string, value: string, extra = '') =>
    `<tr><td style="padding:6px 0;font-size:14px;color:#2b2b2b;${extra}">${esc(label)}</td><td style="padding:6px 0;font-size:14px;color:#2b2b2b;text-align:right;white-space:nowrap;${extra}">${value}</td></tr>`;

  const html = `<table style="width:100%;border-collapse:collapse;">
${rowsHtml}
${hasDiscount ? sumRow(t('quote.page.subtotal'), money(totals.gross), 'padding-top:12px;') + sumRow(discLabel, `− ${money(totals.discount)}`) : ''}
${sumRow(t('quote.page.total'), money(totals.total), 'font-weight:bold;font-size:16px;border-top:2px solid #2b2b2b;')}
</table>
${deposit > 0 ? `<p style="font-size:14px;margin:14px 0 0;">${esc(t('quote.page.deposit'))}: <strong>${money(deposit)}</strong></p>` : ''}`;

  const text = [
    ...rows.map((r) => `- ${r.label}${r.full ? ` (${money(r.full)})` : ''}: ${money(r.amount)}${r.desc ? ` (${r.desc})` : ''}`),
    '',
    ...(hasDiscount ? [`${t('quote.page.subtotal')}: ${money(totals.gross)}`, `${discLabel}: -${money(totals.discount)}`] : []),
    `${t('quote.page.total')}: ${money(totals.total)}`,
    ...(deposit > 0 ? [`${t('quote.page.deposit')}: ${money(deposit)}`] : []),
  ];
  return { html, text };
}

/** Il preventivo semplice, oppure una sezione per ogni proposta (nome, nota, prezzi). */
function pricesBlock(i: QuoteEmailInput, money: (n: number) => string): { html: string; text: string[] } {
  if (!hasVariants(i.event)) return pricesOf(i.event, i, money);
  const html: string[] = [];
  const text: string[] = [];
  for (const v of variantList(i.event)) {
    const p = pricesOf(variantEvent(i.event, v), i, money);
    const title = (v.name ?? '').trim() || i.t('quote.variants.untitled');
    const note = (v.note ?? '').trim();
    html.push(`<p style="font-size:16px;font-weight:bold;margin:20px 0 4px;">${esc(title)}</p>
${note ? `<p style="font-size:14px;color:#7a7266;margin:0 0 8px;">${esc(note)}</p>` : ''}
${p.html}`);
    text.push(title.toUpperCase(), ...(note ? [note] : []), ...p.text, '');
  }
  return { html: html.join('\n'), text };
}

export function buildQuoteEmail(i: QuoteEmailInput): { html: string; text: string } {
  const { event, t, locale } = i;
  const money = (n: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(n);
  const prices = pricesBlock(i, money);
  const name = event?.client?.name ?? '';
  const place = event?.event?.location ?? '';
  const time = event?.event?.time ?? '';
  const notes = clientNotes(event);
  const disclaimer = hasPriceDisclaimer(event) ? t('quote.page.price_disclaimer') : '';

  const intro = t('quote.mail.intro', { date: i.dateDisplay });
  const where = [time, place].filter(Boolean).join(' · ');

  const sig = [i.signature.name, i.signature.phone, i.signature.email].filter(Boolean);

  const html = `<div style="font-family:Georgia,'Times New Roman',serif;max-width:560px;color:#2b2b2b;line-height:1.5;">
${i.logo ? `<p style="margin:0 0 16px;"><img src="${esc(i.logo)}" alt="${esc(i.signature.name)}" style="max-width:180px;max-height:70px;height:auto;"></p>` : ''}
<p style="font-size:15px;margin:0 0 12px;">${esc(t('quote.mail.greeting', { name }))}</p>
<p style="font-size:15px;margin:0 0 4px;">${esc(intro)}</p>
${where ? `<p style="font-size:14px;color:#7a7266;margin:0 0 16px;">${esc(where)}</p>` : '<div style="height:12px;"></div>'}
${prices.html}
${notes ? `<p style="font-size:14px;margin:14px 0 0;white-space:pre-line;"><strong>${esc(t('quote.page.notes'))}</strong><br>${esc(notes)}</p>` : ''}
${disclaimer ? `<p style="font-size:12px;color:#7a7266;margin:14px 0 0;">${esc(disclaimer)}</p>` : ''}
${
  i.link
    ? `<p style="margin:20px 0 6px;"><a href="${esc(i.link)}" style="display:inline-block;background:#2b2b2b;color:#ffffff;text-decoration:none;padding:12px 22px;border-radius:6px;font-size:14px;">${esc(t('quote.mail.cta'))}</a></p>
<p style="font-size:12px;color:#7a7266;margin:0;">${esc(t('quote.page.valid_until'))} ${esc(i.linkExpiry)}</p>`
    : ''
}
<p style="font-size:15px;margin:22px 0 4px;">${esc(t('quote.mail.closing'))}</p>
${sig.length ? `<p style="font-size:14px;margin:0;">${sig.map(esc).join('<br>')}</p>` : ''}
</div>`;

  const lines = [
    t('quote.mail.greeting', { name }),
    '',
    intro,
    where,
    '',
    ...prices.text,
    ...(notes ? ['', `${t('quote.page.notes')}:`, notes] : []),
    ...(disclaimer ? ['', disclaimer] : []),
    ...(i.link ? ['', `${t('quote.mail.cta')}: ${i.link}`, `${t('quote.page.valid_until')} ${i.linkExpiry}`] : []),
    '',
    t('quote.mail.closing'),
    ...sig,
  ];

  return { html, text: lines.join('\n') };
}

export async function copyRichText(html: string, text: string): Promise<boolean> {
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return true;
    }
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
